import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { Radio } from "@/components/base/radio-group";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateMatchQueries } from "@/lib/tournament-cache";

type MatchKind = "set" | "match";

const setSchema = z.object({
  winScore: z.number().int().positive(),
  switchScore: z.number().int().positive(),
});

const schema = z.object({
  matchKind: z.enum<MatchKind[]>(["set", "match"]),
  sets: z.array(setSchema).min(1),
});

const SINGLE_SET_DEFAULTS = [{ winScore: 28, switchScore: 7 }];
const BEST_OF_3_DEFAULTS = [
  { winScore: 21, switchScore: 7 },
  { winScore: 21, switchScore: 7 },
  { winScore: 15, switchScore: 5 },
];

export type UpdateScoreOptionsFormProps = {
  playoffMatchId: number;
  matchName: string;
  currentSets: { winScore: number; switchScore: number | null }[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const DEFAULT_SWITCH_SCORE = 7;

export function UpdateScoreOptionsForm({
  playoffMatchId,
  matchName,
  currentSets,
  onOpenChange,
  ...props
}: UpdateScoreOptionsFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.matches.updateMatchFormat.mutationOptions(),
    onSuccess: () => {
      invalidateMatchQueries(queryClient, trpc);
      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {
      matchKind: (currentSets.length > 1 ? "match" : "set") as MatchKind,
      sets:
        currentSets.length > 0
          ? currentSets.map((s) => ({
              winScore: s.winScore,
              switchScore: s.switchScore ?? DEFAULT_SWITCH_SCORE,
            }))
          : SINGLE_SET_DEFAULTS,
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === "matchKind") {
          formApi.setFieldValue(
            "sets",
            fieldApi.state.value === "set"
              ? SINGLE_SET_DEFAULTS
              : BEST_OF_3_DEFAULTS,
          );
        }
      },
    },
    onSubmit: ({ value: { sets } }) => {
      mutate({ playoffMatchId, sets });
    },
  });

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Update Score Options
        </Heading>

        <p className="text-sm text-gray-600">
          Configure the match format for{" "}
          <span className="font-semibold">{matchName}</span>. Changing the set
          count will discard any scores on removed sets.
        </p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title="Unable to update score options"
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="matchKind"
            children={(field) => (
              <field.RadioGroup
                label="Match Style"
                orientation="horizontal"
                field={field}
                mode="string"
              >
                <Radio value="set">Single Set</Radio>
                <Radio value="match">Best of 3</Radio>
              </field.RadioGroup>
            )}
          />

          <form.AppField name="sets" mode="array">
            {(field) => (
              <div className="flex flex-col space-y-2">
                {field.state.value.map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: form array fields keyed by position
                  <div className="grid grid-cols-6 gap-3" key={i}>
                    <form.AppField name={`sets[${i}].winScore`}>
                      {(subField) => (
                        <subField.Number
                          className="col-span-3"
                          field={subField}
                          label={
                            i === 0 ? "Win Score" : `Set ${i + 1} Win Score`
                          }
                        />
                      )}
                    </form.AppField>
                    <form.AppField name={`sets[${i}].switchScore`}>
                      {(subField) => (
                        <subField.Number
                          className="col-span-3"
                          field={subField}
                          label={
                            i === 0 ? "Switch Every" : `Set ${i + 1} Switch`
                          }
                        />
                      )}
                    </form.AppField>
                  </div>
                ))}
              </div>
            )}
          </form.AppField>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false} isDisabled={isPending}>
                {isPending ? "Saving…" : "Save"}
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
